<template>
  <page-header title="Cluster" :subtitle="subtitle" :identifier="id">
    <template v-if="isLoaded">
      <button v-if="cluster.id" class="btn-outline-primary h-10 mr-2" @click="toggleCluster">
        <template v-if="cluster.isEnabled">
          <EyeSlashIcon class="size-5 -ml-1 mr-2" />
          Disable
        </template>
        <template v-else>
          <EyeIcon class="size-5 -ml-1 mr-2" />
          Enable
        </template>
      </button>
      <button class="btn-primary h-10" :class="{'disabled': isSaving || !isDataChanged || result.$invalid }" @click="save">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Save
      </button>
    </template>
  </page-header>

  <template v-if="isLoaded">
    <form @submit.prevent="save">
      <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <form-input v-model="cluster.name" label="Name" class="md:col-span-6" :error-msg="result.name.$messages.join(' | ')" />

        <form-select
          v-model="cluster.dataCenterId"
          label="Data Center"
          class="md:col-span-6"
          :items="dataCenters"
          option-value="id"
          option-text="name"
          :error-msg="result.dataCenterId.$messages.join(' | ')"
        />

        <form-input
          v-model="cluster.primaryGatewayUrl"
          label="Primary Gateway URL"
          class="md:col-span-4"
          :class="{'opacity-50': !cluster.isPrimaryGataewayEnabled }"
          :error-msg="result.primaryGatewayUrl.$messages.join(' | ')"
        />

        <form-switch v-model="cluster.isPrimaryGataewayEnabled" class="md:col-span-1 mt-6 pt-1 flex items-center" />

        <div class="md:col-span-1 flex flex-col justify-end">
          <button class="btn-outline-primary h-9" @click.prevent="checkGateway('Primary')">
            Check
          </button>
        </div>

        <form-input
          v-model="cluster.reserveGatewayUrl"
          label="Reserve Gateway URL"
          class="md:col-span-4"
          :class="{'opacity-50': !cluster.isReserveGataewayEnabled }"
          :error-msg="result.reserveGatewayUrl.$messages.join(' | ')"
        />

        <form-switch v-model="cluster.isReserveGataewayEnabled" class="md:col-span-1 mt-6 pt-1 flex items-center" />

        <div class="md:col-span-1 flex flex-col justify-end">
          <button class="btn-outline-primary h-9" @click.prevent="checkGateway('Reserve')">
            Check
          </button>
        </div>

        <form-input
          v-model="cluster.storageUrl"
          label="Storage URL"
          class="md:col-span-4"
          :class="{'opacity-50': !cluster.isStorageEnabled }"
          :error-msg="result.storageUrl.$messages.join(' | ')"
        />

        <form-switch v-model="cluster.isStorageEnabled" class="md:col-span-1 mt-6 pt-1 flex items-center" />

        <div class="md:col-span-1 flex flex-col justify-end">
          <button class="btn-outline-primary h-9" @click.prevent="checkStorage">
            Check
          </button>
        </div>

        <form-input
          v-model="cluster.storageCredentials"
          label="Storage Credentials"
          class="md:col-span-4"
          :class="{'opacity-50': !cluster.isStorageEnabled }"
          :error-msg="result.storageCredentials.$messages.join(' | ')"
        />

        <data-center-sharing-gateways
          v-if="cluster.id"
          ref="gatewaysSectionRef"
          :data-center-id="cluster.dataCenterId"
          :cluster-id="cluster.id"
        />
      </div>
    </form>
  </template>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { useToast } from 'vue-toastification';
import { Modal } from '../composables/useModal';
import hashSum from 'hash-sum';
import {
  CircleStackIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/vue/24/outline';
import PageHeader from '../components/PageHeader.vue';
import FormInput from '../components/FormInput.vue';
import FormSelect from '../components/FormSelect.vue';
import FormSwitch from '../components/FormSwitch.vue';
import DataCenterSharingGateways from '../components/DataCenterSharingGateways.vue';
import useValidate from 'vue-tiny-validate';
import { formatBytes } from '../utils/formatters';

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  dataCenterId: {
    type: String,
    default: null
  }
});

const toast = useToast();
const isLoaded = ref(false);
const isSaving = ref(false);
const cluster = ref({});
const clusterHashSum = ref(null);
const dataCenters = ref([]);
const gatewaysSectionRef = ref(null);

const isDataChanged = computed(() => (clusterHashSum.value !== hashSum(cluster.value)) || gatewaysSectionRef.value?.isGatewaysChanged());

const subtitle = computed(() => {
  if (!isLoaded.value) {
    return null;
  }

  return `Storage used: ${formatBytes(cluster.value.storageUsage)}`;
});

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);

  loadDataCenters();

  if (props.id) {
    load();
    options.autoTest = true;

  } else {
    const _dataCenterId = props.dataCenterId || null;

    cluster.value = {
      name: '',
      dataCenterId: _dataCenterId ? Number(_dataCenterId) : null,
      primaryGatewayUrl: '',
      reserveGatewayUrl: '',
      storageUrl: '',
      isEnabled: false,
      isPrimaryGataewayEnabled: false,
      isReserveGataewayEnabled: false,
      isStorageEnabled: false
    };
    clusterHashSum.value = hashSum(cluster.value);

    isLoaded.value = true;

    options.autoTest = true;
  }
});

const rules = reactive({
  name: {
    name: 'name',
    test: value => Boolean(value),
    message: 'Name must not be empty.'
  },
  dataCenterId: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Data Center must be selected.'
  },
  primaryGatewayUrl: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Primary Gateway URL must not be empty.'
  },
  reserveGatewayUrl: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Reserve Gateway URL must not be empty.'
  },
  storageUrl: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Storage URL must not be empty.'
  },
  storageCredentials: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Storage Credentials must not be empty.'
  }
});
const options = reactive({
  autoTest: false
});
const { result } = useValidate(cluster, rules, options);

async function loadDataCenters() {
  const { data } = await useFetch(`${apiDomain}/api/data-center?limit=1000&offset=0`).json().get();

  dataCenters.value = data.value.dataCenters;
}

async function load() {
  const { data, error } = await useFetch(`${apiDomain}/api/cluster/${props.id}`).json().get();

  if (error.value) {
    toast.error('Error loading cluster');
    return;
  }

  cluster.value = data.value;
  clusterHashSum.value = hashSum(cluster.value);
  isLoaded.value = true;
}

async function validate() {
  return await result.value.$test();
}

async function save() {
  isSaving.value = true;

  await validate();
  if (result.value.$invalid) {
    isSaving.value = false;
    return;
  }

  if (clusterHashSum.value !== hashSum(cluster.value)) {
    const { data, error } = await useFetch(`${apiDomain}/api/cluster/`).post(cluster.value).json();

    if (error.value) {
      toast.error('Error saving cluster');
      isSaving.value = false;
      return;
    }

    if (data.value && data.value.id && !cluster.value.id) {
      cluster.value.id = data.value.id;
    }
  }

  if (cluster.value.id && gatewaysSectionRef.value) {
    const gatewaysSaved = await gatewaysSectionRef.value.saveGateways();
    if (!gatewaysSaved) {
      toast.warning('Cluster saved but there were issues with saving sharing gateways');
      isSaving.value = false;
      return;
    }
  }

  clusterHashSum.value = hashSum(cluster.value);
  isSaving.value = false;
  toast.success('Cluster saved successfully');
}

async function toggleCluster() {
  const direction = cluster.value.isEnabled ? 'disable' : 'enable';
  const { data } = await useFetch(`${apiDomain}/api/cluster/${direction}/${cluster.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success(`Cluster ${direction}d successfully`);
    return;
  }

  toast.error(`Error ${direction} Cluster`);
}

function handleBeforeUnload(e) {
  if (!isDataChanged.value || !Object.keys(cluster.value).length) {
    return;
  }
  e.preventDefault();
  e.returnValue = '';
}

onBeforeRouteLeave(async (to, from, next) => {
  if (!isDataChanged.value || !Object.keys(cluster.value).length) {
    return next();
  }

  const confirm = await Modal.confirm({
    title: 'Unsaved changes',
    body: 'You have unsaved changes. <br/> If you leave this page, your changes will be lost. <br/> Are you sure you want to exit without saving?',
    okButton: 'Leave',
    iconColor: 'primary'
  });

  return confirm ? next() : next(false);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

async function checkStorage() {
  if (!cluster.value.storageUrl || !cluster.value.storageCredentials) {
    toast.error('Storage URL and Credentials must be provided to check storage.');
    return;
  }

  const storageUrl = cluster.value.storageUrl;
  const storageCredentials = cluster.value.storageCredentials;

  const { error, data } = await useFetch(`${apiDomain}/api/cluster/check-health-storage`).post({ storageUrl, storageCredentials }).json();

  if (error.value) {
    toast.error(error.value.message || 'Error checking storage health');
    return;
  }

  if (data.value.status !== 200) {
    toast.error(`Storage health check failed: ${data.value.message || 'Unknown error'}`);
    return;
  }

  toast.success('Storage health check successful');
}

async function checkGateway(priority) {
  const url = priority === 'Primary' ? cluster.value.primaryGatewayUrl : cluster.value.reserveGatewayUrl;

  if (!url) {
    toast.error(`${priority} Gateway URL must be provided to check health.`);
    return;
  }

  const { error, data } = await useFetch(`${apiDomain}/api/cluster/check-health-gateway`).post({ url }).json();

  if (error.value) {
    toast.error(`Error checking ${priority} gateway health`);
    return;
  }

  if (data.value.status !== 200) {
    toast.error(`Gateway health check failed: ${data.value.message || 'Unknown error'}`);
    return;
  }

  toast.success(`${priority} Gateway health check successful`);
}
</script>
